import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { OrganizationBody, OrganizationTitle } from '@this/components/organization/organization.style';

import SimpleLoading from '@this/shared/simple_loading/simple_loading';
import EncodingSelector from '@this/shared/encoding_selector/encoding_selector';
import Modal from '@this/shared/modal/modal';
import { Button } from '@this/shared/ui/inputs/button';

import type { InvoiceArgs } from '@this/domain/invoice/invoice2';
import Invoice from '@this/domain/invoice/invoice2';
import type { OrganizationAnnouncementArg } from '@this/domain/organization_announcement/organization_announcement';
import OrganizationAnnouncement from '@this/domain/organization_announcement/organization_announcement';
import InvoiceCustomFormat from '@this/src/domain/invoice_custom_format/invoice_custom_format';
import type { InvoiceCustomFormatArgs } from '@this/domain/invoice_custom_format/invoice_custom_format';
import { Link } from '@this/shared/ui/navigations/link';
import styled, { css } from 'styled-components';
import { Fetcher } from '@this/src/util';
import { reportError } from '@this/lib/bugsnag';
import { InvoiceTable } from './invoice_table/invoice_table';
import {
  CreditCardAccountingDataTable,
  type CreditCardAccountingDataItem
} from './credit_card_accounting_data_table/credit_card_accounting_data_table';
import External from './external/external';

type TabType = 'invoice' | 'credit_card';

interface InvoicesResponse {
  invoices: InvoiceArgs[];
  freee_connected: boolean;
  bugyo_connected: boolean;
  infomart_connected: boolean;
  organization_announcements: OrganizationAnnouncementArg[];
  invoice_custom_formats: InvoiceCustomFormatArgs[];
}

interface CreditCardAccountingDataResponse {
  credit_card_accounting_data: {
    id: number;
    year: number;
    month: number;
    created_at: string;
    aggregation_type: string;
    aggregation_type_label: string;
  }[];
}

interface FooterItemProps {
  backgroundImage: string;
}

const fetchInvoices = async ({
  setLoading,
  setInvoices,
  setFreeeConnected,
  setBugyoConnected,
  setInfomartConnected,
  setOrganizationAnnouncements,
  setInvoiceCustomFormats,
  setCreditCardAccountingData
}: {
  setLoading: (flag: boolean) => void;
  setInvoices: (invoices: Invoice[]) => void;
  setFreeeConnected: (flag: boolean) => void;
  setBugyoConnected: (flag: boolean) => void;
  setInfomartConnected: (flag: boolean) => void;
  setOrganizationAnnouncements: (organizationAnnouncements: OrganizationAnnouncement[]) => void;
  setInvoiceCustomFormats: (invoiceCustomFormats: InvoiceCustomFormat[]) => void;
  setCreditCardAccountingData: (data: CreditCardAccountingDataItem[]) => void;
}) => {
  setLoading(true);
  try {
    const [invoicesRes, creditCardRes] = await Promise.all([
      Fetcher.get<InvoicesResponse>('/organization/invoices.json'),
      Fetcher.get<CreditCardAccountingDataResponse>('/organization/credit_card_accounting_data.json')
    ]);
    setLoading(false);
    setInvoices(invoicesRes.invoices.map(raw => new Invoice(raw)));
    setFreeeConnected(invoicesRes.freee_connected);
    setBugyoConnected(invoicesRes.bugyo_connected);
    setInfomartConnected(invoicesRes.infomart_connected);
    setOrganizationAnnouncements(
      invoicesRes.organization_announcements.map(raw => new OrganizationAnnouncement(raw))
    );
    setInvoiceCustomFormats(invoicesRes.invoice_custom_formats.map(raw => new InvoiceCustomFormat(raw)));
    setCreditCardAccountingData(
      creditCardRes.credit_card_accounting_data.map(raw => ({
        id: raw.id,
        year: raw.year,
        month: raw.month,
        created_at: raw.created_at,
        aggregation_type: raw.aggregation_type,
        aggregation_type_label: raw.aggregation_type_label
      }))
    );
  } catch (e) {
    setLoading(false);
    reportError(e as Error);
  }
};

const Invoices = () => {
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [creditCardAccountingData, setCreditCardAccountingData] = useState<CreditCardAccountingDataItem[]>([]);
  const [encoding, setEncoding] = useState('');
  const [activeTab, setActiveTab] = useState<TabType>('invoice');
  const [showExternalInvoice, setShowExternalInvoice] = useState<Invoice | null>(null);
  const [freeeConnected, setFreeeConnected] = useState(false);
  const [bugyoConnected, setBugyoConnected] = useState(false);
  const [infomartConnected, setInfomartConnected] = useState(false);
  const [organizationAnnouncements, setOrganizationAnnouncements] = useState<OrganizationAnnouncement[]>([]);
  const [invoiceCustomFormats, setInvoiceCustomFormats] = useState<InvoiceCustomFormat[]>([]);

  const hasInvoices = invoices.length > 0;
  const hasCreditCardData = creditCardAccountingData.length > 0;
  const showTabs = hasInvoices && hasCreditCardData;

  useEffect(() => {
    fetchInvoices({
      setLoading,
      setInvoices,
      setFreeeConnected,
      setBugyoConnected,
      setInfomartConnected,
      setOrganizationAnnouncements,
      setInvoiceCustomFormats,
      setCreditCardAccountingData
    });
  }, []);

  const getBackgroundImageUrl = (index: number) => {
    const images = [
      '/images/announcement/announcement_bg_gold.png',
      '/images/announcement/announcement_bg_green.png',
      '/images/announcement/announcement_bg_gray.png'
    ];
    return images[index % images.length];
  };

  const refreshInvoices = () => {
    fetchInvoices({
      setLoading,
      setInvoices,
      setFreeeConnected,
      setBugyoConnected,
      setInfomartConnected,
      setOrganizationAnnouncements,
      setInvoiceCustomFormats,
      setCreditCardAccountingData
    });
  };

  const hasAnyData = hasInvoices || hasCreditCardData;

  const renderContent = () => {
    if (showTabs) {
      if (activeTab === 'invoice') {
        return (
          <InvoiceTable
            invoices={invoices}
            invoiceCustomFormats={invoiceCustomFormats}
            encoding={encoding}
            setShowExternalInvoice={setShowExternalInvoice}
          />
        );
      }
      return <CreditCardAccountingDataTable items={creditCardAccountingData} encoding={encoding} />;
    }
    if (hasInvoices) {
      return (
        <InvoiceTable
          invoices={invoices}
          invoiceCustomFormats={invoiceCustomFormats}
          encoding={encoding}
          setShowExternalInvoice={setShowExternalInvoice}
        />
      );
    }
    if (hasCreditCardData) {
      return <CreditCardAccountingDataTable items={creditCardAccountingData} encoding={encoding} />;
    }
    return null;
  };

  return (
    <div>
      <OrganizationTitle>請求書・明細</OrganizationTitle>
      <OrganizationBody>
        {loading ? (
          <SimpleLoading />
        ) : !hasAnyData ? (
          <div>現在表示可能な請求書はありません</div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              {showTabs && (
                <TabContainer>
                  <TabButton active={activeTab === 'invoice'} onClick={() => setActiveTab('invoice')}>
                    請求書
                  </TabButton>
                  <TabButton active={activeTab === 'credit_card'} onClick={() => setActiveTab('credit_card')}>
                    クレジットカード
                  </TabButton>
                </TabContainer>
              )}
              <EncodingSelector encoding={encoding} onChange={enc => setEncoding(enc)} />
              {hasInvoices && (
                <Button size="small" href="/organization/invoice_custom_formats">
                  カスタムCSV設定
                </Button>
              )}
            </div>
            {renderContent()}
          </>
        )}
      </OrganizationBody>
      <Footer>
        {organizationAnnouncements.map((data, index) => (
          <Link
            key={data.id}
            href={data.url || '/inquiries/new'}
            target="_blank"
            isExternal
            rel="noopener noreferrer"
          >
            <FooterItem backgroundImage={getBackgroundImageUrl(index)}>
              <TitlesContainer>
                <Title1>{data.title1}</Title1>
                <Title2>{data.title2}</Title2>
              </TitlesContainer>
            </FooterItem>
          </Link>
        ))}
      </Footer>
      <Modal
        show={!!showExternalInvoice}
        hideModal={() => setShowExternalInvoice(null)}
        title="請求書の外部サービス連携"
      >
        {showExternalInvoice && (
          <External
            freeeConnected={freeeConnected}
            bugyoConnected={bugyoConnected}
            infomartConnected={infomartConnected}
            invoice={showExternalInvoice}
            onRefreshInvoices={refreshInvoices}
          />
        )}
      </Modal>
    </div>
  );
};

const TabContainer = styled.div`
  display: flex;
  gap: 4px;
`;

const TabButton = styled.button<{ active: boolean }>`
  padding: 8px 16px;
  border: 1px solid #dee2e6;
  background: ${props => (props.active ? '#007bff' : 'transparent')};
  color: ${props => (props.active ? 'white' : '#212529')};
  cursor: pointer;
  border-radius: 4px;
  font-size: 14px;
`;

const Footer = styled.footer`
  display: flex;
  justify-content: flex-start;
  margin: 100px 10px 0 10px;
  gap: 20px;
`;

const FooterItem = styled.div<FooterItemProps>`
  background-image: url(${props => props.backgroundImage});
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  width: 300px;
  height: 300px;
`;

const TitlesContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  height: 70px;
  padding: 0 10px;
`;

const commonTitleStyles = css`
  color: white;
  font-weight: bold;
`;

const Title1 = styled.div`
  ${commonTitleStyles};
  font-size: 11px;
  margin-bottom: 3px;
`;

const Title2 = styled.div`
  ${commonTitleStyles};
  font-size: 13px;
`;

export default observer(Invoices);
